import React, { useState } from 'react';
import {
    Box, Container, Typography, Card, Stack, Button,
    Chip, Avatar, Paper, Divider, Pagination, Fade,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import {
    AccountBalanceWallet, ArrowUpward, ArrowDownward,
    ShoppingCart, ArrowBack, AddCircle,
    RemoveCircle, Undo, CardGiftcard, History,
    ReceiptLong, FilterList
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

// --- Premium Theme Config ---
const primaryGradient = 'linear-gradient(135deg, #FF6A00 0%, #EE0979 100%)';
const darkGradient = 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)';
const lightBg = '#F8FAFC';

// 🌟 DUMMY DATA (Replace these with your actual props/state) 🌟
const wallet = {
    balance: 1250,
    total_earned: 2500,
    total_spent: 1250
};

const transactions = [
    { id: 1, created_at: '2026-02-27T10:30:00', type: 'purchase', description: 'Credit Purchase', amount: 500, balance_after: 1250, reference_type: 'PAY_12345' },
    { id: 2, created_at: '2026-02-26T14:15:00', type: 'deduction', description: 'Credit Used for Design', amount: -50, balance_after: 750, reference_type: 'TPL_889' },
    { id: 3, created_at: '2026-02-25T09:00:00', type: 'refund', description: 'Refund Processed', amount: 50, balance_after: 800, reference_type: 'REF_998' },
    { id: 4, created_at: '2026-02-24T18:45:00', type: 'bonus', description: 'Welcome Bonus', amount: 100, balance_after: 750, reference_type: 'PROMO_1' },
];

const pagination = {
    page: 1,
    totalPages: 3,
    total: 25
};

export default function CreditTransactions() {
    const [currentFilter, setCurrentFilter] = useState('all');

    // Handle filter changes (Simulating your URL logic)
    const handleFilterChange = (newFilter) => {
        setCurrentFilter(newFilter);
        // In real app: navigate(`?type=${newFilter}&page=1`);
    };

    // Date Formatter Helper (Similar to your Handlebars formatDate)
    const formatDate = (dateString, formatType) => {
        const date = new Date(dateString);
        if (formatType === 'time') {
            return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
        }
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    // Helper for Transaction Icons & Colors based on your original logic
    const getTxnMeta = (type) => {
        switch (type) {
            case 'purchase': return { icon: <AddCircle fontSize="small" />, color: '#10B981', bg: '#ECFDF5', label: 'Purchase' };
            case 'deduction': return { icon: <RemoveCircle fontSize="small" />, color: '#EF4444', bg: '#FEF2F2', label: 'Used' };
            case 'refund': return { icon: <Undo fontSize="small" />, color: '#3B82F6', bg: '#EFF6FF', label: 'Refund' };
            case 'bonus': return { icon: <CardGiftcard fontSize="small" />, color: '#F59E0B', bg: '#FFFBEB', label: 'Bonus' };
            default: return { icon: <History fontSize="small" />, color: '#64748B', bg: '#F1F5F9', label: type };
        }
    };

    return (
        <Box sx={{ bgcolor: lightBg, minHeight: '100vh', pb: 12 }}>

            {/* 🌟 HERO SECTION 🌟 */}
            <Box sx={{ pt: { xs: 8, md: 10 }, pb: { xs: 10, md: 12 }, background: darkGradient, position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '60%', height: '3px', background: primaryGradient, boxShadow: '0 0 20px 5px rgba(238, 9, 121, 0.4)' }} />
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                    <History sx={{ fontSize: { xs: 50, md: 60 }, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
                    <Typography variant="h3" fontWeight="900" color="white" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, mb: 1 }}>
                        क्रेडिट लेन-देन / Credit Transactions
                    </Typography>
                    <Typography variant="body1" color="#94A3B8" fontWeight="500">
                        अपने सभी क्रेडिट लेन-देन का विवरण देखें <br /> View details of all your credit transactions
                    </Typography>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ mt: { xs: -5, md: -6 }, position: 'relative', zIndex: 2 }}>


                {wallet && (
                    <Card sx={{ p: 0, borderRadius: 4, boxShadow: '0 8px 25px rgba(0,0,0,0.05)', border: '1px solid #E2E8F0', mb: 4, overflow: 'hidden' }}>
                        {/* 👇 FIX: flexDirection को हमेशा 'row' कर दिया है */}
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>

                            {/* Balance */}
                            <Box sx={{ flex: 1, p: { xs: 1.5, md: 3 }, bgcolor: 'white', textAlign: 'center', borderRight: '1px solid #E2E8F0' }}>
                                <Avatar sx={{ bgcolor: '#EFF6FF', color: '#3B82F6', width: { xs: 40, md: 56 }, height: { xs: 40, md: 56 }, mx: 'auto', mb: { xs: 1, md: 2 } }}>
                                    <AccountBalanceWallet sx={{ fontSize: { xs: 20, md: 30 } }} />
                                </Avatar>
                                <Typography variant="h3" fontWeight="900" color="#3B82F6" sx={{ fontSize: { xs: '1.4rem', md: '2.5rem' }, lineHeight: 1 }}>
                                    {wallet.balance}
                                </Typography>
                                <Typography variant="caption" color="#64748B" fontWeight="600" sx={{ fontSize: { xs: '0.7rem', md: '0.9rem' }, mt: 0.5, display: 'block' }}>
                                    Balance
                                </Typography>
                            </Box>

                            {/* Earned */}
                            <Box sx={{ flex: 1, p: { xs: 1.5, md: 3 }, bgcolor: 'white', textAlign: 'center', borderRight: '1px solid #E2E8F0' }}>
                                <Avatar sx={{ bgcolor: '#ECFDF5', color: '#10B981', width: { xs: 40, md: 56 }, height: { xs: 40, md: 56 }, mx: 'auto', mb: { xs: 1, md: 2 } }}>
                                    <ArrowUpward sx={{ fontSize: { xs: 20, md: 30 } }} />
                                </Avatar>
                                <Typography variant="h3" fontWeight="900" color="#10B981" sx={{ fontSize: { xs: '1.4rem', md: '2.5rem' }, lineHeight: 1 }}>
                                    {wallet.total_earned}
                                </Typography>
                                <Typography variant="caption" color="#64748B" fontWeight="600" sx={{ fontSize: { xs: '0.7rem', md: '0.9rem' }, mt: 0.5, display: 'block' }}>
                                    Earned
                                </Typography>
                            </Box>

                            {/* Spent */}
                            <Box sx={{ flex: 1, p: { xs: 1.5, md: 3 }, bgcolor: 'white', textAlign: 'center' }}>
                                <Avatar sx={{ bgcolor: '#FEF2F2', color: '#EF4444', width: { xs: 40, md: 56 }, height: { xs: 40, md: 56 }, mx: 'auto', mb: { xs: 1, md: 2 } }}>
                                    <ArrowDownward sx={{ fontSize: { xs: 20, md: 30 } }} />
                                </Avatar>
                                <Typography variant="h3" fontWeight="900" color="#EF4444" sx={{ fontSize: { xs: '1.4rem', md: '2.5rem' }, lineHeight: 1 }}>
                                    {wallet.total_spent}
                                </Typography>
                                <Typography variant="caption" color="#64748B" fontWeight="600" sx={{ fontSize: { xs: '0.7rem', md: '0.9rem' }, mt: 0.5, display: 'block' }}>
                                    Spent
                                </Typography>
                            </Box>

                        </Box>
                    </Card>
                )}

                {/* 🌟 FILTERS & ACTIONS (Matching your original filter options) 🌟 */}
                <Card sx={{ p: 2.5, borderRadius: 3, mb: 3, boxShadow: 'none', border: '1px solid #E2E8F0', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 2 }}>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', md: 'auto' }, overflowX: 'auto', '::-webkit-scrollbar': { display: 'none' } }}>
                        <Typography variant="body2" fontWeight="700" color="#1E293B" sx={{ display: 'flex', alignItems: 'center', minWidth: 'max-content' }}>
                            <FilterList sx={{ fontSize: 18, mr: 0.5 }} /> Filter by Type:
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            {[
                                { val: 'all', label: 'All Transactions' },
                                { val: 'purchase', label: 'Credit Purchase' },
                                { val: 'deduction', label: 'Credit Used' },
                                { val: 'refund', label: 'Refunds' },
                                { val: 'bonus', label: 'Bonus Credits' }
                            ].map((opt) => (
                                <Chip
                                    key={opt.val}
                                    label={opt.label}
                                    onClick={() => handleFilterChange(opt.val)}
                                    sx={{
                                        fontWeight: 'bold', cursor: 'pointer',
                                        bgcolor: currentFilter === opt.val ? '#1E293B' : '#F1F5F9',
                                        color: currentFilter === opt.val ? 'white' : '#475569',
                                        '&:hover': { bgcolor: currentFilter === opt.val ? '#0F172A' : '#E2E8F0' }
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>

                    <Stack direction="row" spacing={1.5} width={{ xs: '100%', md: 'auto' }}>
                        <Button component={Link} to="/my-profile" variant="outlined" startIcon={<ArrowBack />} sx={{ flex: { xs: 1, md: 'none' }, borderRadius: 2, color: '#475569', borderColor: '#CBD5E1' }}>
                            Back
                        </Button>
                        <Button component={Link} to="/plans" variant="contained" startIcon={<ShoppingCart />} sx={{ flex: { xs: 1, md: 'none' }, background: primaryGradient, borderRadius: 2, boxShadow: 'none' }}>
                            Buy Credits
                        </Button>
                    </Stack>

                </Card>

                {/* 🌟 TRANSACTIONS LIST 🌟 */}
                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                    <Box sx={{ p: 2.5, bgcolor: 'white', borderBottom: '1px solid #E2E8F0' }}>
                        <Typography variant="h6" fontWeight="800" color="#1E293B" sx={{ display: 'flex', alignItems: 'center' }}>
                            <ReceiptLong sx={{ mr: 1, color: '#3B82F6' }} /> Transaction History / लेन-देन इतिहास
                        </Typography>
                    </Box>

                    {transactions && transactions.length > 0 ? (
                        <>
                            {/* DESKTOP TABLE */}
                            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                <TableContainer>
                                    <Table>
                                        <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: '700', color: '#475569' }}>Date / तारीख</TableCell>
                                                <TableCell sx={{ fontWeight: '700', color: '#475569' }}>Type / प्रकार</TableCell>
                                                <TableCell sx={{ fontWeight: '700', color: '#475569' }}>Description / विवरण</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: '700', color: '#475569' }}>Amount / राशि</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: '700', color: '#475569' }}>Balance / बैलेंस</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {transactions.map((txn) => {
                                                const meta = getTxnMeta(txn.type);
                                                const isPositive = txn.amount > 0;
                                                return (
                                                    <TableRow key={txn.id} sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight="700">{formatDate(txn.created_at, 'date')}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{formatDate(txn.created_at, 'time')}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip icon={meta.icon} label={meta.label} size="small" sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 'bold' }} />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight="600">{txn.description}</Typography>
                                                            {txn.reference_type && <Typography variant="caption" color="text.secondary">Ref: {txn.reference_type}</Typography>}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography variant="body1" fontWeight="800" color={isPositive ? '#10B981' : '#EF4444'}>
                                                                {isPositive ? '+' : ''}{txn.amount}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography variant="body2" fontWeight="700">{txn.balance_after}</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>

                            {/* MOBILE LIST */}
                            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                {transactions.map((txn, index) => {
                                    const meta = getTxnMeta(txn.type);
                                    const isPositive = txn.amount > 0;
                                    return (
                                        <Box key={txn.id} sx={{ p: 2, borderBottom: index === transactions.length - 1 ? 'none' : '1px solid #F1F5F9' }}>
                                            <Box display="flex" justifyContent="space-between" mb={1}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Chip icon={meta.icon} label={meta.label} size="small" sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 'bold', height: 24, fontSize: '0.7rem' }} />
                                                    <Typography variant="caption" color="text.secondary">{formatDate(txn.created_at, 'date')}</Typography>
                                                </Box>
                                                <Typography variant="subtitle1" fontWeight="800" color={isPositive ? '#10B981' : '#EF4444'}>
                                                    {isPositive ? '+' : ''}{txn.amount}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" fontWeight="600" mb={0.5}>{txn.description}</Typography>
                                            <Box display="flex" justifyContent="space-between">
                                                {txn.reference_type ? <Typography variant="caption" color="text.secondary">Ref: {txn.reference_type}</Typography> : <span />}
                                                <Typography variant="caption" fontWeight="600" color="text.secondary">Bal: {txn.balance_after}</Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>

                            {/* PAGINATION */}
                            {pagination && pagination.totalPages > 1 && (
                                <Box sx={{ bgcolor: '#F8FAFC', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', borderTop: '1px solid #E2E8F0' }}>
                                    <Pagination count={pagination.totalPages} page={pagination.page} color="primary" shape="rounded" />
                                    <Typography variant="caption" color="text.secondary" mt={1}>
                                        Showing {pagination.page} of {pagination.totalPages} pages ({pagination.total} total transactions)
                                    </Typography>
                                </Box>
                            )}
                        </>
                    ) : (
                        /* NO TRANSACTIONS EMPTY STATE */
                        <Box textAlign="center" py={6}>
                            <History sx={{ fontSize: 60, color: '#CBD5E1', mb: 2 }} />
                            <Typography variant="h6" fontWeight="700" color="#1E293B" mb={1}>कोई लेन-देन नहीं मिला / No Transactions Found</Typography>
                            <Typography variant="body2" color="text.secondary" mb={3}>आपके पास अभी तक कोई क्रेडिट लेन-देन नहीं है।</Typography>
                            <Button component={Link} to="/plans" variant="contained" startIcon={<ShoppingCart />} sx={{ borderRadius: 2 }}>
                                Buy Your First Credits
                            </Button>
                        </Box>
                    )}

                </Card>

            </Container>
        </Box>
    );
}